import React, { useCallback, useEffect, useRef, useState } from "react";
import { getFirebaseAuth } from "@/lib/firebase";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { toast } from "sonner";
import { Phone, ShieldCheck, CheckCircle2 } from "lucide-react";

/**
 * Reusable phone-OTP verifier (Firebase Phone Auth).
 *
 * Two-step handshake:
 *   1. Enter phone → click "Send code" → Firebase SMSs a 6-digit OTP.
 *   2. Enter OTP → click "Verify" → we get an ID token → surfaced via
 *      `onVerified(idToken, e164Phone)`.
 *
 * The parent decides what to do with the token (create/update user, or log
 * in). This component stays presentation-only and does NOT touch our own
 * backend — that's the parent's job.
 *
 * If the user edits the phone number after verifying, the verified state is
 * cleared and a fresh OTP round-trip is required.
 */
export function PhoneOtpVerifier({
  phone,
  onPhoneChange,
  onVerified,
  disabled = false,
  autoFocus = false,
  testidPrefix = "phone-otp",
  hidePhoneInput = false,
}) {
  const [otp, setOtp] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [verifiedFor, setVerifiedFor] = useState(null); // phone that was last verified
  const [setupError, setSetupError] = useState(null);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const recaptchaContainerRef = useRef(null);
  const recaptchaVerifierRef = useRef(null);

  const isVerified = verifiedFor && verifiedFor === phone;

  // Clear verified/sent state whenever the phone edit invalidates prior OTP flow.
  useEffect(() => {
    if (sent && verifiedFor !== phone) {
      setSent(false);
      setOtp("");
      setConfirmationResult(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone]);

  const clearRecaptcha = useCallback(() => {
    try { recaptchaVerifierRef.current?.clear(); } catch { /* ignore */ }
    recaptchaVerifierRef.current = null;
    if (recaptchaContainerRef.current) recaptchaContainerRef.current.innerHTML = "";
  }, []);

  // Fresh DOM node per attempt avoids "reCAPTCHA has already been rendered in this element".
  const ensureRecaptcha = useCallback(() => {
    clearRecaptcha();
    const el = document.createElement("div");
    recaptchaContainerRef.current.appendChild(el);
    recaptchaVerifierRef.current = new RecaptchaVerifier(getFirebaseAuth(), el, { size: "invisible" });
    return recaptchaVerifierRef.current;
  }, [clearRecaptcha]);

  useEffect(() => clearRecaptcha, [clearRecaptcha]);

  const sendOtp = async () => {
    setSetupError(null);
    if (!phone || !phone.startsWith("+") || phone.length < 8) {
      toast.error("Enter phone in E.164 format (e.g. +918921211564)");
      return;
    }
    setBusy(true);
    try {
      const auth = getFirebaseAuth();
      const verifier = ensureRecaptcha();
      const result = await signInWithPhoneNumber(auth, phone.trim(), verifier);
      setConfirmationResult(result);
      setSent(true);
      toast.success("OTP sent");
    } catch (err) {
      const code = err?.code || "";
      const msg = err?.message || "Failed to send OTP";
      if (code === "auth/configuration-not-found" || /configuration-not-found/i.test(msg)) {
        setSetupError("Phone provider not enabled in Firebase Console");
      } else if (code === "auth/unauthorized-domain" || /unauthorized-domain/i.test(msg)) {
        setSetupError(`Add this domain to Firebase authorized domains: ${window.location.hostname}`);
      } else if (code === "auth/invalid-phone-number") {
        toast.error("Invalid phone number. Include country code, e.g. +918921211564");
      } else if (code === "auth/too-many-requests" || /too-many-requests/i.test(msg)) {
        toast.error("Too many attempts. Wait a few minutes.");
      } else {
        toast.error(msg);
      }
      clearRecaptcha();
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async () => {
    if (!confirmationResult) {
      toast.error("Please request a fresh code first");
      return;
    }
    if (otp.length !== 6) {
      toast.error("Enter the 6-digit code");
      return;
    }
    setBusy(true);
    try {
      const credential = await confirmationResult.confirm(otp.trim());
      const idToken = await credential.user.getIdToken();
      setVerifiedFor(phone);
      onVerified?.(idToken, phone);
      toast.success("Phone verified");
    } catch (err) {
      toast.error(err?.message?.includes("invalid") ? "Invalid or expired code" : "Verification failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-root`}>
      {!hidePhoneInput && (
        <div>
          <label className="label-eyebrow mb-2 flex items-center gap-1">
            <Phone className="w-3 h-3" /> Phone (E.164) *
          </label>
          <div className="flex gap-2">
            <input
              data-testid={`${testidPrefix}-phone-input`}
              type="tel"
              value={phone}
              onChange={(e) => onPhoneChange(e.target.value)}
              className="flex-1 bg-white border border-zinc-300 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7] font-mono disabled:bg-zinc-100"
              placeholder="+918921211564"
              disabled={disabled || isVerified}
              autoFocus={autoFocus}
            />
            {!isVerified && (
              <button
                type="button"
                onClick={sendOtp}
                disabled={disabled || busy || !phone}
                data-testid={`${testidPrefix}-send-button`}
                className="px-3 py-2 border border-zinc-300 text-[11px] uppercase tracking-wider hover:bg-zinc-50 disabled:opacity-50 font-semibold whitespace-nowrap"
              >
                {busy && !sent ? "Sending..." : sent ? "Resend" : "Send OTP"}
              </button>
            )}
          </div>
          {isVerified && (
            <div
              className="mt-2 flex items-center gap-1 text-[11px] font-mono text-emerald-700 uppercase tracking-wider"
              data-testid={`${testidPrefix}-verified-badge`}
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Verified
            </div>
          )}
        </div>
      )}

      {sent && !isVerified && (
        <div className="border border-zinc-200 bg-zinc-50 p-3 space-y-2">
          <label className="label-eyebrow flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" /> Enter 6-digit code sent to {phone}
          </label>
          <div className="flex gap-2">
            <input
              data-testid={`${testidPrefix}-otp-input`}
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              className="flex-1 bg-white border border-zinc-300 text-base tracking-[0.4em] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7] font-mono text-center"
              placeholder="000000"
              autoFocus
            />
            <button
              type="button"
              onClick={verifyOtp}
              disabled={busy || otp.length !== 6}
              data-testid={`${testidPrefix}-verify-button`}
              className="px-3 py-2 bg-zinc-950 text-white text-[11px] uppercase tracking-wider hover:bg-zinc-800 disabled:opacity-50 font-semibold"
            >
              {busy ? "Verifying..." : "Verify"}
            </button>
          </div>
        </div>
      )}

      {setupError && (
        <div className="border border-rose-300 bg-rose-50 text-rose-950 text-xs px-3 py-2" data-testid={`${testidPrefix}-setup-error`}>
          <span className="font-semibold uppercase tracking-wider text-[11px] text-rose-800">Firebase setup: </span>
          {setupError}
        </div>
      )}

      <div ref={recaptchaContainerRef} data-testid={`${testidPrefix}-recaptcha`} />
    </div>
  );
}
